/**
 * UPBEAT / APUA Color Palette System
 *
 * All palette values are CSS variables mapped directly to design/tokens.css,
 * ensuring design/tokens.css remains the single source of truth.
 */

/**
 * Categorical color sequence for multi-country / multi-series comparison.
 * Maps directly to the CSS design tokens --series-1 through --series-8.
 */
export const COMPARE_COLORS: readonly string[] = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
  'var(--series-7)',
  'var(--series-8)',
];

/** Alias for existing imports across the project */
export const DEFAULT_COMPARE_COLORS = COMPARE_COLORS;

/** Returns the color assigned to a series by zero-based index */
export function getCompareColor(index: number): string {
  return COMPARE_COLORS[index % COMPARE_COLORS.length]!;
}

/**
 * Standard cartographic diverging palette for choropleth heatmaps (ColorBrewer RdYlGn 5-stop).
 */
export const MAP_HEATMAP = {
  bad: '#D73027',
  badMid: '#F46D43',
  neutral: '#FEE08B',
  goodMid: '#A6D96A',
  good: '#1A9850',
} as const;

export const MAP_HEATMAP_STOPS: readonly string[] = [
  MAP_HEATMAP.bad,
  MAP_HEATMAP.badMid,
  MAP_HEATMAP.neutral,
  MAP_HEATMAP.goodMid,
  MAP_HEATMAP.good,
];

/**
 * Category/Component color tokens for radar background sectors and badges.
 * References theme CSS variables.
 */
export const COMPONENT_COLORS: Record<string, string> = {
  'Capital Structure': 'var(--series-8)',
  'Cost Recovery': 'var(--series-6)',
  'Liquidity': 'var(--series-5)',
  'Profitability': 'var(--gold)',
  'Subsidies and resource extraction': 'var(--ember)',
  'Efficiency': 'var(--brand-blue)',
  'Reliability': 'var(--good)',
};

export function getComponentColor(component: string): string {
  return COMPONENT_COLORS[component] ?? 'var(--chart-axis)';
}

/** Named palette tokens for dashboard visualization */
export const PALETTE = {
  /** Brand primary */
  primary: 'var(--gold)',
  /** Brand secondary text */
  primaryText: 'var(--gold2)',
  /** Brand ember */
  ember: 'var(--ember)',
  /** Continental African benchmark line / marker */
  benchmark: 'var(--chart-axis)',
  /** Excellence frontier marker */
  frontier: 'var(--gold)',
  /** Status positive / higher is better */
  good: 'var(--good)',
  /** Status negative / worse */
  bad: 'var(--bad)',
  /** Map positive & negative ramp bounds */
  mapGood: MAP_HEATMAP.good,
  mapBad: MAP_HEATMAP.bad,
};
